import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Check, Loader2 } from "lucide-react";
import { DEPOSIT_LOOKUP, BUILD_PACKAGES } from "@/components/BuildConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEPOSIT_AMOUNT = { essentials: 200, full_pour: 320, zero_proof: 160 };

export default function Confirmation() {
  const [params] = useSearchParams();
  const type = params.get("type") || "enquiry";
  const ref = params.get("ref") || "";
  const pkg = params.get("pkg") || "";
  const isBooking = type === "booking";
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (typeof window.gtag === "function") {
      window.gtag("event", "generate_lead", {
        lead_type: isBooking ? "booking" : "quote",
        reference: ref || "unknown",
      });
    }
  }, []);

  const summary = (() => {
    try {
      const d = JSON.parse(sessionStorage.getItem("ss_booking") || "null");
      return d && d.reference === ref ? d : null;
    } catch {
      return null;
    }
  })();

  const payDeposit = async () => {
    setPaying(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: DEPOSIT_LOOKUP[pkg],
        origin_url: window.location.origin,
        reference: ref,
      });
      window.location.href = data.checkout_url;
      return;
    } catch (err) {
      setPaying(false);
      import("sonner").then(({ toast }) => toast.error("Could not start checkout — we'll email you a payment link."));
    }
  };

  const pkgName = BUILD_PACKAGES.find((p) => p.id === pkg)?.name;

  return (
    <section data-testid="confirmation-page" className="grain flex min-h-screen items-center justify-center bg-espresso-deep px-5 py-24 sm:px-8">
      <div className="w-full max-w-2xl text-center">
        <span className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-gold text-espresso shadow-[0_20px_50px_-20px_rgba(197,158,74,0.8)]">
          <Check className="h-9 w-9" strokeWidth={3} />
        </span>

        <h1 className="mt-10 font-display text-4xl text-cream sm:text-5xl lg:text-6xl" data-testid="confirmation-heading">
          {isBooking ? (
            <>We received your <em className="italic text-rose">booking</em></>
          ) : (
            <>We received your <em className="italic text-rose">request</em></>
          )}
        </h1>

        <p className="mx-auto mt-6 max-w-md text-sm leading-relaxed text-cream/70 sm:text-base" data-testid="confirmation-copy">
          {isBooking
            ? "Thank you — your date is on our calendar. We'll confirm everything within 24 hours and send your deposit invoice to lock it in."
            : "Thank you — your quote is brewing. Expect to hear from us within 24 hours."}
        </p>

        {ref && (
          <p className="mt-8 inline-block rounded-full border border-cream/20 px-6 py-2 text-sm tracking-[0.2em] text-gold" data-testid="confirmation-ref">
            REF: {ref}
          </p>
        )}

        {summary && (
          <div className="mx-auto mt-10 max-w-md rounded-2xl border border-cream/15 bg-white/[0.03] p-6 text-left" data-testid="confirmation-summary">
            {summary.date && (
              <p className="flex justify-between gap-4 border-b border-cream/10 py-2 text-sm">
                <span className="text-cream/50">Date</span>
                <span className="text-cream">{summary.date}</span>
              </p>
            )}
            {summary.venue && (
              <p className="flex justify-between gap-4 border-b border-cream/10 py-2 text-sm">
                <span className="text-cream/50">Occasion</span>
                <span className="text-cream">{summary.venue}</span>
              </p>
            )}
            {summary.guests && (
              <p className="flex justify-between gap-4 py-2 text-sm">
                <span className="text-cream/50">Guests</span>
                <span className="text-cream">{summary.guests}</span>
              </p>
            )}
          </div>
        )}

        {isBooking && pkg && (
          <div className="mt-10">
            <button
              data-testid="confirmation-pay-btn"
              onClick={payDeposit}
              disabled={paying}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-gold px-10 py-4 text-sm font-semibold uppercase tracking-[0.18em] text-espresso transition-all duration-300 hover:scale-[1.02] hover:bg-cream disabled:opacity-60"
            >
              {paying && <Loader2 className="h-4 w-4 animate-spin" />}
              {paying
                ? "Opening checkout..."
                : `Pay your 40% deposit — $${DEPOSIT_AMOUNT[pkg] || ""}`}
            </button>
            <p className="mt-3 text-[11px] tracking-wide text-cream/40">
              Paid securely through Stripe · fully refundable 14+ days before your event
            </p>
          </div>
        )}

        <div className="mt-12">
          <Link
            to="/"
            data-testid="confirmation-home-btn"
            className="inline-block rounded-full border border-cream/40 px-8 py-3 text-sm font-semibold uppercase tracking-[0.18em] text-cream transition-colors hover:border-gold hover:text-gold"
          >
            Back to home
          </Link>
        </div>
      </div>
    </section>
  );
}
